export const SS_KEY_SELECTED_DELIVERY_ADDRESS_PREFIX = 'SELFSERVICE_SELECTED_DELIVERY_ADDRESS';

const storageKey = (accountNumber: string) => `${SS_KEY_SELECTED_DELIVERY_ADDRESS_PREFIX}_${accountNumber}`;

export const normalizeAddressSearchParam = (searchParamAddress: string | null | undefined): string | null => {
  if (!searchParamAddress) return null;
  if (searchParamAddress.endsWith('/')) {
    return searchParamAddress.split('/')[0];
  }
  return searchParamAddress;
};

export const getSelectedDeliveryAddressId = (accountNumber: string): string | null => {
  if (typeof window === 'undefined' || !accountNumber) return null;
  try {
    return sessionStorage.getItem(storageKey(accountNumber));
  } catch {
    return null;
  }
};

export const setSelectedDeliveryAddressId = (accountNumber: string, addressId: string): void => {
  if (typeof window === 'undefined' || !accountNumber || !addressId) return;
  try {
    sessionStorage.setItem(storageKey(accountNumber), addressId);
  } catch {
    // sessionStorage may be unavailable (private browsing quota, etc.)
  }
};

export const clearSelectedDeliveryAddressId = (accountNumber: string): void => {
  if (typeof window === 'undefined' || !accountNumber) return;
  try {
    sessionStorage.removeItem(storageKey(accountNumber));
  } catch {
    // ignore
  }
};

export const clearAllSelectedDeliveryAddressIds = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const prefix = `${SS_KEY_SELECTED_DELIVERY_ADDRESS_PREFIX}_`;
    const keysToRemove: string[] = [];
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
};

export interface ResolveDeliveryAddressIdentifierOptions {
  accountNumber: string;
  addressOptionValues: string[];
  searchParamAddress: string | null;
}

/**
 * Resolves delivery address id: URL param (if valid) > sessionStorage (if valid) > first option.
 */
export const resolveDeliveryAddressIdentifier = ({
  accountNumber,
  addressOptionValues,
  searchParamAddress,
}: ResolveDeliveryAddressIdentifierOptions): string => {
  if (addressOptionValues.length === 0) return '';
  if (addressOptionValues.length === 1) return addressOptionValues[0];

  const fromUrl = normalizeAddressSearchParam(searchParamAddress);
  if (fromUrl && addressOptionValues.includes(fromUrl)) {
    return fromUrl;
  }

  const fromStorage = getSelectedDeliveryAddressId(accountNumber);
  if (fromStorage && addressOptionValues.includes(fromStorage)) {
    return fromStorage;
  }

  if (fromStorage) {
    clearSelectedDeliveryAddressId(accountNumber);
  }

  return addressOptionValues[0];
};

export interface ResolveDeliveryAddressFromQueryOptions {
  accountNumber: string;
  queryAddress: string | string[] | undefined;
}

/**
 * Resolves address from router query when address options are not available (URL > sessionStorage).
 */
export const resolveDeliveryAddressFromQuery = ({
  accountNumber,
  queryAddress,
}: ResolveDeliveryAddressFromQueryOptions): string | undefined => {
  const rawParam = Array.isArray(queryAddress) ? queryAddress[0] : queryAddress;
  const fromUrl = normalizeAddressSearchParam(rawParam ?? null);
  if (fromUrl) return fromUrl;
  return getSelectedDeliveryAddressId(accountNumber) ?? undefined;
};
